
import React, { useState } from 'react';
import { useVoiceCommands } from '@/contexts/VoiceCommandContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { VoiceCommandCategory } from '@/types/voiceCommand';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';

const VoiceCommandList: React.FC = () => {
  const { commands, state, toggleCommandList } = useVoiceCommands();
  const { showCommandList } = state;
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<VoiceCommandCategory | 'all'>('all');
  
  // Get unique categories from commands
  const categories = Array.from(new Set(commands.map(cmd => cmd.category)));
  
  // Filter commands based on search query and active tab
  const filteredCommands = commands.filter(cmd => {
    const matchesSearch = 
      cmd.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cmd.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cmd.phrases.some(p => p.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (cmd.examples?.some(e => e.toLowerCase().includes(searchQuery.toLowerCase())) ?? false);
    
    const matchesCategory = activeTab === 'all' || cmd.category === activeTab;
    
    return matchesSearch && matchesCategory;
  });
  
  return (
    <Dialog open={showCommandList} onOpenChange={toggleCommandList}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Available Voice Commands</DialogTitle>
          <DialogDescription>
            Say "help" anytime to see this list
          </DialogDescription>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search commands..."
            className="pl-8"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        
        <Tabs defaultValue="all" value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
          <TabsList className="w-full overflow-x-auto flex-wrap">
            <TabsTrigger value="all">All</TabsTrigger>
            {categories.map(category => (
              <TabsTrigger key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </TabsTrigger>
            ))}
          </TabsList>
          
          <TabsContent value="all" className="mt-4">
            <div className="space-y-4">
              {filteredCommands.map(command => (
                <CommandCard key={command.name} command={command} />
              ))}
              {filteredCommands.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No commands found matching your search
                </div>
              )}
            </div>
          </TabsContent>
          
          {categories.map(category => (
            <TabsContent key={category} value={category} className="mt-4">
              <div className="space-y-4">
                {filteredCommands.map(command => (
                  <CommandCard key={command.name} command={command} />
                ))}
                {filteredCommands.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No commands found matching your search
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

interface CommandCardProps {
  command: {
    name: string;
    phrases: string[];
    category: VoiceCommandCategory;
    description: string;
    examples?: string[];
  };
}

const CommandCard: React.FC<CommandCardProps> = ({ command }) => {
  return (
    <div className="rounded-lg border p-4">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-semibold">{command.name}</h3>
        <Badge variant="outline" className="capitalize">
          {command.category}
        </Badge>
      </div>
      
      <p className="text-sm text-muted-foreground mb-2">{command.description}</p>
      
      <div className="mt-3">
        <h4 className="text-xs font-medium mb-1">Trigger phrases:</h4>
        <div className="flex flex-wrap gap-1">
          {command.phrases.map(phrase => (
            <Badge key={phrase} variant="secondary" className="text-xs">
              "{phrase}"
            </Badge>
          ))}
        </div>
      </div>
      
      {command.examples && command.examples.length > 0 && (
        <div className="mt-3">
          <h4 className="text-xs font-medium mb-1">Examples:</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            {command.examples.map((example, index) => (
              <li key={index}>• "{example}"</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VoiceCommandList;
